/**
 * `?rate=` — reaching S-12's declined-instalments variants before the dev overlay exists.
 *
 * Three treatments are described for a declined instalment decision and no source
 * chooses between them (BR-019 / E-14 / D-16; `schema.graphql` records `q: "none"` on
 * `declinedOutcome`). All three are built on S-12, and this is how they are reached:
 *
 * ```
 * ?rate=block     → BLOCK_DEVICE            the handset cannot be added
 * ?rate=upfront   → OFFER_FULL_PRICE        keep it, pay outright
 * ?rate=sim       → DOWNGRADE_TO_SIM_ONLY   drop to Journey A
 * ?rate=none      → declined with no treatment at all — the state the sources leave
 * ?rate=fail      → the engine does not answer at all — no decision was taken
 * ?rate=normal    → the eligible path, and clears a held switch
 * ```
 *
 * Two things happen for each of the first three. `AdapterModes.set('installments',
 * 'degraded')` is what makes the port decline at all — `InMemoryInstallmentsPort`
 * returns `DECLINED` only in degraded mode, because a decline is the service working
 * and saying no, not the service being down. `setDeclinedBehaviour` then picks which
 * of the three answers it gives.
 *
 * `none` is the honest default of the source material rather than an extra: the engine
 * declines and nothing says what follows, so S-12 shows the decision, states that the
 * consequence is undefined and offers no route onward. It is reachable on purpose —
 * a state nobody can see is a state nobody will answer.
 *
 * `fail` is a different kind of state again: the port errors, S-12 shows the retry card,
 * and the screen is explicit that no decision was recorded about the company. A decline
 * and an outage look nothing alike to a customer and must not be reachable only as each
 * other.
 *
 * ### Why it is held in `sessionStorage`, against the adapter's own rule
 *
 * `AdapterModes` is deliberately not persisted, and its reasoning is good: a forced
 * failure you switched on to show a colleague should not still be on the next morning.
 * This switch persists anyway, for one screen, because the query string does not
 * survive the first in-app navigation and S-12 is two screens past the one you can put
 * a query string on — `?rate=block` on `/migrare/oferte` would be gone by the time the
 * catalogue loaded.
 *
 * The tension is real and resolved narrowly: it lives in `sessionStorage`, so it dies
 * with the tab rather than the browser profile; `clear()` drops it; and
 * `DemoControls.reset()` clears the adapter mode independently. The dev panel is now a
 * second caller — `SwitchBoard` pairs `setRateSwitch` with `applyRateSwitch` so the
 * change takes effect without a reload — and the URL form stays, because the Playwright
 * specs drive forced states by URL rather than by clicking through a panel; the same
 * arrangement as `?terms=a|b`.
 *
 * This is POC scaffolding. Nothing in production has a switch for which of three
 * undefined behaviours applies; production has whichever one Orange chooses.
 */
import { inject, provideAppInitializer } from '@angular/core';
import type { EnvironmentProviders } from '@angular/core';
import type { DemoControls } from '../../mock-bff/mock-backend';
import { MockBackend } from '../../mock-bff/mock-backend';

/**
 * The three declined treatments, the absence of one, the outage, and the way back to
 * the eligible path.
 */
export type RateSwitch = 'normal' | 'block' | 'upfront' | 'sim' | 'none' | 'fail';

const STORAGE_KEY = 'pre2post.poc.rate.v1';

const BEHAVIOUR = {
  block: 'BLOCK_DEVICE',
  upfront: 'OFFER_FULL_PRICE',
  sim: 'DOWNGRADE_TO_SIM_ONLY',
  // Not an omission: the wire field is nullable and this is what it carries when
  // nothing has been decided.
  none: null,
} as const;

/**
 * Applies a held or URL-supplied switch at startup.
 *
 * `MockBackend` is injected here and in `GraphqlClient`, and nowhere else in
 * `src/app`: the app talks GraphQL to the mock BFF and knows nothing else about it.
 * These two files are the seam, which is what makes `mock-bff/` deletable.
 */
export function provideInstalmentOutcomeSwitch(): EnvironmentProviders {
  return provideAppInitializer(() => {
    const active = read();
    if (active === null || active === 'normal') return;
    // Resolved through the injector rather than constructed: `MockBackend` holds the
    // repository and the clock, and a second instance would be a second set of orders.
    applyRateSwitch(inject(MockBackend).controls, active);
  });
}

/** Holds the choice for the rest of the tab's life. Persists only; see the header. */
export function setRateSwitch(value: RateSwitch): void {
  try {
    if (value === 'normal') sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Private browsing. The switch applies to this page load and no further.
  }
}

export function clearRateSwitch(): void {
  setRateSwitch('normal');
}

/**
 * The effect, separated from the persistence so both callers can have exactly one of
 * them: the initializer applies a value it read, and the panel applies a value it is
 * also holding. Takes `DemoControls` rather than resolving `MockBackend` itself, so it
 * is callable outside an injection context — the panel is a component, not a provider.
 */
export function applyRateSwitch(controls: DemoControls, value: RateSwitch): void {
  if (value === 'normal') {
    controls.adapterModes.set('installments', 'normal');
    return;
  }
  if (value === 'fail') {
    controls.adapterModes.set('installments', 'fail');
    return;
  }
  controls.adapterModes.set('installments', 'degraded');
  controls.setDeclinedBehaviour(BEHAVIOUR[value]);
}

/** What is held right now. The panel's display value; `normal` when nothing is. */
export function currentRateSwitch(): RateSwitch {
  try {
    return parseRateSwitch(sessionStorage.getItem(STORAGE_KEY) ?? '') ?? 'normal';
  } catch {
    return 'normal';
  }
}

/** The URL wins over the held value, and replaces it. */
function read(): RateSwitch | null {
  try {
    const fromUrl = new URLSearchParams(location.search).get('rate');
    if (fromUrl !== null) {
      const parsed = parseRateSwitch(fromUrl);
      if (parsed !== null) {
        setRateSwitch(parsed);
        return parsed;
      }
    }
    return parseRateSwitch(sessionStorage.getItem(STORAGE_KEY) ?? '');
  } catch {
    return null;
  }
}

export function parseRateSwitch(value: string): RateSwitch | null {
  return value === 'normal' ||
    value === 'block' ||
    value === 'upfront' ||
    value === 'sim' ||
    value === 'none' ||
    value === 'fail'
    ? value
    : null;
}
